import type { HTMLAttributes } from "react";
import { cn } from "@/lib/cn";

/**
 * Section-opener label.
 *
 * Per the Stabiliti brand guide: Poppins SemiBold, uppercase, wide
 * letter-spacing. The "amber eyebrow" treatment from the layout guide
 * is one of three tones — used sparingly for marketing-flavoured
 * section openers (e.g. Stories, Outcomes hero). For day-to-day section
 * labels prefer the quieter "muted" or "atlantic" tones.
 *
 * Pops of colour should feel intentional, not frequent.
 */
export interface EyebrowProps extends HTMLAttributes<HTMLSpanElement> {
  /** Default = Peat (quiet, neutral). Amber = marketing pop. Atlantic = link-flavoured. */
  tone?: "muted" | "amber" | "atlantic";
}

const TONE: Record<NonNullable<EyebrowProps["tone"]>, string> = {
  muted:    "var(--text-subtle)",
  amber:    "var(--brand-amber)",
  atlantic: "var(--brand-atlantic)",
};

export function AmberEyebrow({
  children,
  tone = "muted",
  className,
  style,
  ...props
}: EyebrowProps) {
  return (
    <span
      className={cn(
        "inline-block font-brand text-[11px] font-semibold uppercase tracking-[0.16em]",
        className,
      )}
      style={{ color: TONE[tone], ...style }}
      {...props}
    >
      {children}
    </span>
  );
}
